import { useEffect, useState } from "react";
import { Flame, Target, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";

function Ring({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
      <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
      <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
    </svg>
  );
}

export default function DailyGoal({ className = "" }) {
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/goals/today").then((r) => { setG(r.data); setVal(String(r.data.daily_pages)); }).catch(() => {});
  }, [user]);

  if (!user || !g) return null;

  const save = async () => {
    const n = parseInt(val, 10);
    if (!n || n < 1 || n > 500) { toast.error("pick 1–500 pages"); return; }
    await api.put("/goals", { daily_pages: n });
    setG({ ...g, daily_pages: n });
    setEditing(false);
    toast.success("daily goal updated");
  };

  const pct = Math.min(1, g.pages_today / g.daily_pages);
  const done = pct >= 1;

  return (
    <div className={`border border-border rounded-md bg-card p-5 paper-shadow flex items-center gap-5 ${className}`} data-testid="daily-goal-card">
      <div className="relative shrink-0">
        <Ring pct={pct} />
        <div className="absolute inset-0 grid place-items-center font-display text-lg" data-testid="daily-goal-progress">
          {g.pages_today}/{g.daily_pages}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 font-ui text-xs tracking-caps text-muted-foreground">
          <Target className="w-3.5 h-3.5 text-primary" /> today&apos;s goal
          {!editing && (
            <button onClick={() => setEditing(true)} data-testid="edit-goal-btn" className="ml-1 hover:text-primary" title="edit goal">
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
        {editing ? (
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} className="w-24 h-8 font-ui" data-testid="goal-input" />
            <span className="font-ui text-xs text-muted-foreground">pages / day</span>
            <button onClick={save} data-testid="save-goal-btn" className="p-1.5 rounded-full bg-primary text-primary-foreground"><Check className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <div className="font-reader text-base mt-1">
            {done ? "goal met — beautifully done." : `${g.daily_pages - g.pages_today} pages to go today.`}
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-2 font-ui text-xs text-muted-foreground" data-testid="streak-display">
          <Flame className={`w-3.5 h-3.5 ${g.current_streak_days ? "text-primary" : ""}`} />
          {g.current_streak_days}-day streak · best {g.longest_streak_days} · {g.minutes_today} min today
        </div>
      </div>
    </div>
  );
}
